import { useFormikContext } from "formik";
import { useEffect } from "react";
import { Box, Button, TextField, Typography } from "@mui/material";
import DevelopmentStageSelect from "./DevelopmentStageSelect";
import SDGSelect from "./SDGSelect";
import DateSelect from "../../../common/DateSelect";
import CoordinatesInput from "./CoordinatesInput";
import ReadyForSaleCheckbox from "./ReadyForSaleCheckbox";
import CarbonOffsetCheckbox from "./CarbonOffsetCheckbox";
import InterventionsContainer from "./interventions";
import OutcomesContainer from "./outcomes";
import ProgrammeInfoContainer from "./programme-info";
import InternalDetailsContainer from "./internal-details";
import RiskMonitoringContainer from "./risk-monitoring";
import {
  interventionTypeOptions,
  isUnitOfSaleOutcomeFilledOut,
  isUnitOfSaleSelected,
  outcomesTouched,
  mappedMethodologyField,
} from "../../utils";
import { AddSolutionFormValues } from "../../types";
import CountryMultiSelect from "./CountryMultiSelect";
import SingleSelect from "../../../common/SingleSelect";

export default function WriteableSolutionFormContainer({
  handleCancel,
  submitLoading,
  title,
  page,
}: {
  handleCancel: () => void;
  submitLoading: boolean;
  title: string;
  page: string;
}) {
  const {
    handleSubmit,
    handleChange,
    values,
    touched,
    errors,
    isValid,
    resetForm,
    setFieldTouched,
  } = useFormikContext<AddSolutionFormValues>();

  const hasBiodiversityUnitOfSale = values.outcomes?.some(
    (outcome) => outcome.unit === "Biodiversity" && outcome["unit-of-sale"],
  );

  useEffect(() => {
    if (hasBiodiversityUnitOfSale) {
      setFieldTouched("solution-vbc-website-display-name", true);
    }
  }, [hasBiodiversityUnitOfSale, setFieldTouched]);

  return (
    <>
      <Box
        component="form"
        onSubmit={handleSubmit}
        data-testid="solution-form"
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Typography variant="h4" sx={{ paddingBottom: 2, paddingTop: 2 }}>
          {title}
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            width: "50%",
          }}
        >
          <TextField
            id="solution-name"
            label="Name"
            variant="standard"
            sx={{ paddingBottom: 2 }}
            autoComplete="disabled"
            onChange={handleChange}
            value={values["solution-name"]}
            error={touched["solution-name"] && Boolean(errors["solution-name"])}
            helperText={touched["solution-name"] && errors["solution-name"]}
          />
          <TextField
            id="solution-vbc-website-display-name"
            label="VBC Website Display Name"
            variant="standard"
            sx={{ paddingBottom: 2 }}
            autoComplete="disabled"
            onChange={handleChange}
            value={values["solution-vbc-website-display-name"]}
            error={
              touched["solution-vbc-website-display-name"] &&
              Boolean(errors["solution-vbc-website-display-name"])
            }
            helperText={
              touched["solution-vbc-website-display-name"] &&
              errors["solution-vbc-website-display-name"]
            }
          />
          <Box sx={{ paddingBottom: 2 }}>
            <CountryMultiSelect />
          </Box>
          <TextField
            slotProps={{ htmlInput: { "aria-label": "Short Description" } }}
            id="solution-short-description"
            label="Short Description"
            variant="standard"
            rows={4}
            multiline
            sx={{ paddingBottom: 2 }}
            autoComplete="disabled"
            onChange={handleChange}
            value={values["solution-short-description"]}
          />
          <TextField
            id="solution-developer"
            label="Developer"
            variant="standard"
            sx={{ paddingBottom: 2 }}
            autoComplete="disabled"
            onChange={handleChange}
            value={values["solution-developer"]}
          />
          <Box sx={{ paddingBottom: 2 }}>
            <DevelopmentStageSelect />
          </Box>
          <Box sx={{ paddingBottom: 2 }}>
            <SingleSelect
              options={mappedMethodologyField}
              name={"assessment-methodology"}
              label={"Assessment Methodology"}
              fieldPath={"solution-assessment-methodology"}
            />
          </Box>
          <Box sx={{ paddingBottom: 2 }}>
            <SDGSelect />
          </Box>
          <Box sx={{ paddingBottom: 2 }}>
            <DateSelect<"solution">
              dateName="solution"
              minDate={new Date("01/01/2018")}
              maxDate={new Date("01/01/2222")}
            />
          </Box>
          <Box sx={{ paddingBottom: 2 }}>
            <CoordinatesInput />
          </Box>
          <Box sx={{ paddingBottom: 2 }}>
            <SingleSelect
              name={"intervention-type"}
              label={"Intervention Type"}
              options={interventionTypeOptions}
              fieldPath={"solution-intervention-type"}
            />
          </Box>
          <Box
            sx={{
              display: "flex",
              direction: "row",
              alignItems: "start",
              paddingBottom: 2,
            }}
          >
            <ReadyForSaleCheckbox />
          </Box>
          <Box
            sx={{
              display: "flex",
              direction: "row",
              alignItems: "start",
              paddingBottom: 4,
            }}
          >
            <CarbonOffsetCheckbox />
          </Box>
          <Box
            id="intervention-container"
            sx={{
              paddingBottom: 4,
            }}
          >
            <InterventionsContainer open />
          </Box>
          <Box
            id="outcomes-container"
            sx={{
              paddingBottom: 4,
            }}
          >
            <OutcomesContainer open />
          </Box>
          <Box
            id="programme-info-container"
            sx={{
              paddingBottom: 4,
            }}
          >
            <ProgrammeInfoContainer />
          </Box>
          <Box
            id="internal-details-container"
            sx={{
              paddingBottom: 4,
            }}
          >
            <InternalDetailsContainer open />
          </Box>

          <Box
            id="risk-monitoring-container"
            sx={{
              paddingBottom: 4,
            }}
          >
            <RiskMonitoringContainer open page={page} />
          </Box>
        </Box>
        <Box
          sx={{
            backgroundColor: "transparent",
            display: "flex",
            flexDirection: "row",
            justifyContent: "end",
            width: "50%",
            position: "relative",
            bottom: 3,
            paddingRight: 2,
            marginBottom: page === "AddSolutionPage" ? 2.5 : 0,
          }}
        >
          <Button
            onClick={() => {
              handleCancel();
              resetForm();
            }}
            variant="outlined"
            sx={{ mr: 1 }}
          >
            Cancel
          </Button>
          <Button
            data-testid="submit-solution-button"
            loading={submitLoading}
            disabled={
              !isValid ||
              (outcomesTouched(values) &&
                (!isUnitOfSaleSelected(values) ||
                  !isUnitOfSaleOutcomeFilledOut(values)))
            }
            type="submit"
            variant="contained"
          >
            Submit
          </Button>
        </Box>
      </Box>
    </>
  );
}
